import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Droplets, Flame, Biohazard, ShieldAlert, Hammer, Umbrella, Wrench, MapPin, Clock, PhoneCall, FileCheck2, Users } from "lucide-react";
export default function BaltimoreCountyServices() {
  const serviceCards = [{
    title: "Water Damage Restoration & Structural Drying",
    icon: Droplets,
    linkText: "Water Damage & Flood Restoration",
    linkTo: "/WaterDamageFloodRestorationPage",
    body: "From pressurized supply line ruptures in mature Towson and Catonsville homes to torrential runoff penetrating finished basements along the I-695 Beltway, our emergency crews deploy commercial desiccant dehumidifiers and high-volume air movers. We track moisture migration behind plaster, drywall, and subfloors with calibrated thermal imaging, stabilizing structural framing before warping or dry rot takes hold."
  }, {
    title: "24/7 Emergency Flood Cleanup & Extraction",
    icon: Umbrella,
    body: "Baltimore County's dense clay soils drain slowly, creating severe hydrostatic pressure that forces groundwater through foundation cold joints and basement slabs during intense Mid-Atlantic storm fronts. TriServe mobilizes truck-mounted extraction equipment and submersible pumps to eliminate standing floodwaters rapidly, averting catastrophic structural settling and pervasive secondary water damage."
  }, {
    title: "Sewage Backup Cleanup & Sanitization",
    icon: Biohazard,
    linkText: "Sewage Cleanup",
    linkTo: "/SewageCleanupPage",
    body: "Municipal sewer line surcharges, tree root intrusions in aging suburban laterals, and stormwater backflow introduce hazardous Category 3 blackwater into living areas. We establish airtight negative-pressure containment barriers, extract biohazard sludge, remove contaminated porous materials, and apply hospital-grade EPA-registered antimicrobials to eliminate pathogens safely."
  }, {
    title: "Fire & Smoke Damage Restoration",
    icon: Flame,
    linkText: "Fire & Smoke",
    linkTo: "/FireSmokeOdorRestorationPage",
    body: "Beyond initial flame suppression, acidic soot residue and synthetic smoke vapor rapidly etch glass, corrode metals, and saturate wall cavities in Baltimore County properties. Our response teams deliver immediate security board-ups, structural stabilization, aqueous soot mitigation, and thermal fogging deodorization to salvage architectural finishes and permanently neutralize smoke odors."
  }, {
    title: "Mold Remediation & Containment",
    icon: ShieldAlert,
    linkText: "Mold Remediation",
    linkTo: "/MoldRemediationPage",
    body: "Elevated summer humidity paired with persistent below-grade foundation seepage creates an ideal breeding ground for microbial colonies within 48 hours. TriServe erects sealed physical containment zones, operates industrial HEPA air scrubbers under continuous negative pressure, extracts colonized drywall, and applies fungicidal treatments to restore indoor air quality."
  }, {
    title: "Structural Reconstruction & Repair",
    icon: Hammer,
    linkText: "Reconstruction",
    linkTo: "/ReconstructionDesignBuildPage",
    body: "When severe fire char, deep storm impact, or prolonged water saturation necessitates major tear-out, TriServe manages complete turnkey rebuilding. Our licensed tradespeople handle structural framing, subflooring, drywall installation, precision carpentry, and mechanical integration under unified oversight so property owners avoid managing multiple conflicting contractors."
  }, {
    title: "Emergency Property Restoration & Pipe Rupture Response",
    icon: Wrench,
    body: "Harsh winter freeze-thaw cycles across northern Baltimore County and aging copper or cast-iron lines across older residential pockets frequently trigger catastrophic pipe splits. Mobile crews arrive equipped to isolate compromised lines, execute rapid water shutoffs, pump out flooded utility spaces, and initiate immediate structural drying 24/7/365."
  }];
  return <section id="baltimorecountyservices" data-nav="light" className="bg-background text-foreground py-16 sm:py-20 lg:py-24 border-b border-border">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* County Overview Header */}
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 rounded-md bg-muted px-3 py-1 text-xs font-semibold text-foreground mb-4">
            <MapPin className="size-3.5 text-primary" />
            Baltimore County Rapid Deployment
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight text-foreground">
            Emergency Property Restoration Services in Baltimore County
          </h2>
          <p className="mt-4 text-base sm:text-lg text-muted-foreground leading-relaxed">
            When structural disasters strike, rapid intervention prevents compounding damage. TriServe
            deploys fully equipped mobile restoration units directly to your site across the I-695
            Beltway corridor with an approximate 60-minute deployment window. We do not operate a walk-in
            office in Maryland, ensuring all crew resources remain deployed on mobile emergency response.
            You speak directly with our local restoration responders—never an outsourced call center—and
            we coordinate detailed moisture logs, itemized estimates, and claim documentation directly
            with your insurance adjuster.
          </p>
        </div>

        {/* Operating Principles Bar */}
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 p-6 bg-muted/60 rounded-xl border border-border">
          <div className="flex items-start gap-3">
            <Clock className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm text-foreground">
                ~60-Minute Mobile Deployment
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Crews staged for immediate transit along I-695, I-83, and I-795.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <Users className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm text-foreground">
                Local Crews, No Call Centers
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Direct phone communication with on-duty technicians handling your loss.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <FileCheck2 className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm text-foreground">
                Direct Insurance Coordination
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                We provide moisture logs, scope sheets, and adjuster walk-throughs.
              </p>
            </div>
          </div>
          <div className="flex items-start gap-3">
            <PhoneCall className="size-5 text-primary shrink-0 mt-0.5" />
            <div>
              <div className="font-semibold text-sm text-foreground">
                24/7 Dispatch: 855-435-6631
              </div>
              <p className="text-xs text-muted-foreground mt-0.5">
                Mobile crews deployed day or night for residential and commercial losses.
              </p>
            </div>
          </div>
        </div>

        {/* Services Grid */}
        <div className="mt-14">
          <div className="mb-8">
            <h3 className="text-2xl font-bold tracking-tight text-foreground">
              Comprehensive Mitigation & Restoration Disciplines
            </h3>
            <p className="mt-2 text-sm sm:text-base text-muted-foreground">
              From historic home plumbing failures in Catonsville and Towson to heavy storm flooding
              near industrial waterfronts in Dundalk, our technicians manage full-lifecycle disaster
              mitigation.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {serviceCards.map((service, index) => {
            const IconComponent = service.icon;
            const isPipeRuptureCard = service.title === "Emergency Property Restoration & Pipe Rupture Response";
            if (isPipeRuptureCard) {
              return <Card key={service.title} data-index={index} className="border-border bg-card shadow-xs md:col-span-2 lg:col-span-3 p-6 sm:p-8">
                    <div className="flex flex-col md:flex-row gap-6 items-center">
                      <div className="w-full md:w-1/2 flex flex-col justify-between">
                        <div>
                          <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                            <IconComponent className="size-5" />
                          </div>
                          <CardTitle className="text-xl font-semibold text-card-foreground">
                            <h3 className="text-xl font-semibold text-card-foreground leading-snug">
                              {service.title}
                            </h3>
                          </CardTitle>
                          <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-2">
                            {service.body}
                          </CardDescription>
                        </div>
                        <div className="pt-4 sm:pt-6">
                          {service.linkTo && service.linkText ? <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                              <Link to={service.linkTo}>
                                Explore {service.linkText}
                              </Link>
                            </Button> : <div className="text-xs font-medium text-muted-foreground">
                              Full-service on-site mitigation & inspection
                            </div>}
                        </div>
                      </div>
                      <div className="w-full md:w-1/2 self-stretch min-h-[220px] sm:min-h-[260px]">
                        <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/baltimore_county_emergency_pipe_rupture_water_extraction.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/baltimore_county_emergency_pipe_rupture_water_extraction.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1801371/img/baltimore_county_emergency_pipe_rupture_water_extraction.png-wvc-sizes" alt="TriServe restoration technicians performing emergency water extraction and pipe rupture cleanup with professional drying equipment in a Baltimore County, MD property" loading="lazy" decoding="async" width={600} height={400} className="w-full h-full object-cover rounded-lg border border-border" />
                      </div>
                    </div>
                  </Card>;
            }
            return <Card key={service.title} data-index={index} className="border-border bg-card shadow-xs flex flex-col justify-between">
                  <CardHeader>
                    <div className="size-10 rounded-lg bg-primary/10 text-primary flex items-center justify-center mb-3">
                      <IconComponent className="size-5" />
                    </div>
                    <CardTitle className="text-xl font-semibold text-card-foreground">
                      <h3 className="text-xl font-semibold text-card-foreground leading-snug">
                        {service.title}
                      </h3>
                    </CardTitle>
                    <CardDescription className="text-sm text-muted-foreground leading-relaxed pt-2">
                      {service.body}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="pt-2">
                    {service.linkTo && service.linkText ? <Button asChild variant="outline" size="sm" className="w-full sm:w-auto">
                        <Link to={service.linkTo}>
                          Explore {service.linkText}
                        </Link>
                      </Button> : <div className="text-xs font-medium text-muted-foreground">
                        Full-service on-site mitigation & inspection
                      </div>}
                  </CardContent>
                </Card>;
          })}
          </div>
        </div>

        {/* Action Row / CTA Block */}
        <div className="mt-14 sm:mt-16 p-6 sm:p-8 rounded-xl bg-secondary text-secondary-foreground flex flex-col sm:flex-row items-start sm:items-center justify-between gap-6 shadow-md">
          <div>
            <div className="text-xl sm:text-2xl font-bold">
              Dealing with active water, fire, or mold damage?
            </div>
            <p className="mt-1 text-sm text-secondary-foreground/80 max-w-xl">
              Connect directly with an on-duty TriServe responder right now at{" "}
              <span className="font-semibold text-secondary-foreground">855-435-6631</span>. Our mobile
              Baltimore County restoration vehicles deploy immediately with no call-center delays.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-3">
            <Button asChild variant="default" size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90">
              <Link to="/ContactRequestServicePage">
                Request Emergency Service
              </Link>
            </Button>
            <Button asChild variant="outline" size="lg" className="border-secondary-foreground/20 text-secondary-foreground hover:bg-secondary-foreground/10 bg-transparent">
              <Link to="/OurProcessPage">
                View Our Process
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}